import { IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import BottomPanel from "../../../../components/app/panels/BottomPanel";
import { ScrollAnchor } from "../../../../components/ui/ScrollAnchor";
import { JournalBandBadge } from "../../../../components/ui/JournalBandBadge";
import { cn } from "../../../../lib/utils";
import {
  articleBand,
  indexedIn,
} from "../../../../domains/journal/journalRank";
import {
  designLabel,
  useReadingTranslation,
  StudyAppraisalLedger,
  JournalStandingSection,
  journalStatesAnything,
  type ShownAppraisal,
  type JournalStanding,
  type ReadingLanguage,
} from "../../../../components/works-score/WorksScoreScreen";
import type { ArticlePageArticle } from "../../hooks/useArticleData";

/** Whether the detail sheet is open, and which of its two halves it opens
 * on — the teaser's two tap targets deep-link into one merged scroll, so the
 * whole sheet state stays a single discriminant. */
export type WorksScoreSheetTarget = "closed" | "evidence" | "journal";

// ── The sheet ─────────────────────────────────────────────────────────────────

function WorksScoreSheet({
  level,
  language,
  journal,
  target,
  onTargetChange,
}: {
  level: ShownAppraisal | null;
  // The language the work is being read in, which is the language its
  // appraisal is stated in (`ReadingLanguage`).
  language: ReadingLanguage;
  // Resolved ONCE by the teaser (the article-anchored band) and passed down,
  // so the strip and the sheet can never disagree about journal availability.
  journal: JournalStanding | null;
  // Both halves share one scroll, so this only decides which half the sheet
  // opens on — never what it contains.
  target: WorksScoreSheetTarget;
  onTargetChange: (target: WorksScoreSheetTarget) => void;
}) {
  return (
    <BottomPanel
      isOpen={target !== "closed"}
      onClose={() => {
        onTargetChange("closed");
      }}
      displayCloseButton
      backdropBlur
    >
      <div className="flex flex-col px-5 pb-6 pt-10">
        {journal !== null && (
          <ScrollAnchor
            isActive={target === "journal"}
            className="flex flex-col gap-2"
          >
            <JournalStandingSection {...journal} />
          </ScrollAnchor>
        )}
        {level !== null && (
          <ScrollAnchor
            isActive={target === "evidence"}
            className={`flex flex-col gap-2 ${
              journal !== null ? "mt-5 border-t border-ds-black pt-5" : ""
            }`}
          >
            {/* SIGN-OFF (Menie): the appraisal half opens on WHAT the paper
                studied — design and the PICO facts as peer rows — with the
                risk of bias as its last and quietest row. That row EXPANDS in
                place: the reading opens under it and the card stays, so the
                reader keeps the study in view while reading the verdict. A
                verdict is not the headline of a card whose subject is the
                study.
                The ledger, not the card directly: the GPT source sheet renders
                the same ledger, so composing it here is what keeps the two
                surfaces stating the same facts in the same order. */}
            <StudyAppraisalLedger level={level} language={language} />{" "}
          </ScrollAnchor>
        )}
      </div>
    </BottomPanel>
  );
}

/**
 * The works-score teaser on the article Overview tab: the journal-rank badge,
 * the STUDY DESIGN, and deep-linked tap-throughs to the detail sheet, which
 * opens scrolled to the half that was tapped (design half → the appraisal,
 * badge half → Journal). The conduct verdict is NOT here: it is one row of the
 * card inside the sheet, under the design and the result it was read against.
 * Self-gating on data (renders nothing while the detail payload has not
 * landed); the feature flag is read by the pages.
 *
 * An article with no conduct verdict falls to the journal-strip-only card, and
 * its sheet to the journal half alone (SIGN-OFF): a verdict is the whole claim
 * this half makes, so there is nothing to show without one. The api owns that
 * rule for every surface: `workAppraisal` projects a verdictless state to null.
 */
export function ArticleSectionWorksScore({
  article,
  layout,
  sheetTarget,
  onSheetTargetChange,
}: {
  article: ArticlePageArticle;
  /**
   * `desktop` draws the row as the desktop article's "grade-banner" (Figma
   * "D3.explore.article"): 62px high, "Journal Rank", the md band square.
   */
  layout: "phone" | "desktop";
  sheetTarget: WorksScoreSheetTarget;
  onSheetTargetChange: (target: WorksScoreSheetTarget) => void;
}) {
  const { t } = useTranslation();
  // The teaser's appraisal strings follow the WORK, like the sheet behind them
  // (docs/data-ingestion/risk-of-bias.md). Only these: the journal half beside
  // them states venue context, which is the app talking, not the paper. Two
  // renderings of one study design on one screen is the inconsistency this
  // whole change exists to remove.
  const readingT = useReadingTranslation(article.language);
  // The JOURNAL half needs no flag: an impact factor, a band and an indexing
  // list are public facts about the venue. The LEVEL half keeps
  // WEBAPP_RISK_OF_BIAS as its kill switch. With the level absent the teaser
  // already falls to its journal-strip-only card, and the sheet to its
  // journal half alone.
  const showLevel = useFeatureIsOn("WEBAPP_RISK_OF_BIAS");
  // ROLLOUT: the client-room rule is not read here. The page names the room it
  // is read in (`articleDetailQuery`), and the api projects `appraisal` to null
  // inside a client's room (SIGN-OFF: Menie). This surface reads the same null
  // it already handles for a verdictless work.
  const level = showLevel ? article.appraisal : null;
  const rank = article.journalRank;
  const bandRow =
    rank === null ? null : articleBand(article.medical_specialties, rank);
  const band = bandRow === null ? null : bandRow.band;

  // SIGN-OFF (Menie): when the band cannot be anchored to one of the article's
  // own specialties, the percentile is worded against "General medicine"
  // rather than naming a specialty the article is not about.
  // Null when the venue states nothing: the strip below is the only way into
  // the sheet, so offering it for an empty standing opens a blank sheet.
  const standing: JournalStanding | null =
    rank === null
      ? null
      : {
          journalName: article.journal === null ? null : article.journal.name,
          impactFactor: rank.impactFactor,
          // An unbanded journal still has an impact factor and its indexing;
          // the section drops itself when it truly has nothing to state.
          band:
            bandRow === null
              ? null
              : {
                  band: bandRow.band,
                  specialty:
                    bandRow.specialtyName === null
                      ? t("General medicine")
                      : bandRow.specialtyName,
                },
          indexing: indexedIn(rank),
          isCddRecommended: rank.cdd,
        };
  const journalStanding =
    standing !== null && journalStatesAnything(standing) ? standing : null;

  // Nothing to state: no settled level, and no journal Juisci holds.
  if (level === null && journalStanding === null) return null;

  const sheet = (
    <WorksScoreSheet
      level={level}
      language={article.language}
      journal={journalStanding}
      target={sheetTarget}
      onTargetChange={onSheetTargetChange}
    />
  );

  // The journal, as ONE row shared by both paths. `grade-banner`: the rank
  // named, the impact factor under it, the mark on the right, no card and no
  // tint. It was already the whole card when there is no verdict; beside a
  // verdict it is the second row of the same list, so it is drawn once here
  // rather than twice in two idioms.
  //
  // An unbanded venue keeps the row: about a fifth of the journals Juisci
  // holds carry an impact factor with no specialty to rank them within, and
  // the sheet still states that factor, the indexing and the CDD
  // recommendation. With no band the row drops the mark and names the fact it
  // does have.
  // The journal's CONTENT, wrapped differently by each layout below: a
  // full-width row when it stands alone, a column when it shares the line with
  // the appraisal. Written once so the two cannot word the venue differently.
  //
  // An unbanded venue keeps its place: about a fifth of the journals Juisci
  // holds carry an impact factor with no specialty to rank them within, and
  // the sheet still states that factor, the indexing and the CDD
  // recommendation. With no band it drops the mark and names the fact it has.
  // The desktop banner is 62px, its content held to the top 6px down.
  const rowPadding =
    layout === "desktop" ? "min-h-15.5 items-start py-1.5" : "py-3";
  const journalInner =
    journalStanding === null ? null : (
      <>
        <span className="flex min-w-0 flex-col">
          <span className="text-footnote/snug font-semibold text-ds-black">
            {band === null
              ? t("Journal")
              : layout === "desktop"
                ? t("Journal Rank")
                : t("Journal rank")}
          </span>
          {journalStanding.impactFactor !== null && (
            <span className="text-caption/snug font-bold text-ds-moss-600">
              {t("IF {{value}}", {
                value: journalStanding.impactFactor.toFixed(1),
              })}
            </span>
          )}
        </span>
        {band !== null && (
          <JournalBandBadge
            band={band}
            variant={layout === "desktop" ? "md" : "sm"}
          />
        )}
      </>
    );

  // No verdict: the venue alone, and then it is NOT a card. A bordered box and
  // a tinted fill both belong to the two-part card; around one line of fact
  // they read as a stub of it, at twice the height.
  if (level === null) {
    return (
      <section
        data-gtm-section="evidence-level"
        data-gtm-order="16"
        className="border-b-0! pb-0!"
      >
        <button
          type="button"
          onClick={() => {
            onSheetTargetChange("journal");
          }}
          className={cn(
            "flex w-full items-center justify-between gap-3 text-left active:opacity-70",
            rowPadding
          )}
        >
          {journalInner}
        </button>
        {sheet}
      </section>
    );
  }

  return (
    <section
      data-gtm-section="evidence-level"
      data-gtm-order="16"
      className="border-b-0! pb-0!"
    >
      {/* ONE row, two columns, a hairline between them. The journal leads:
          it is the fact every work carries, and the one production states
          today. Each column names itself on the left with its detail beneath,
          and puts its value on the right: the rank mark for the venue, the
          rising signal and the magnitude for the appraisal. Each column is its
          own tap target into its own half of the sheet.
          The columns are `flex-1` and `min-w-0`, so a work with no journal
          gives the appraisal the whole line, and a long instrument name wraps
          inside its column instead of pushing the row wider. */}
      <div className="flex w-full items-stretch gap-3">
        {journalInner !== null && (
          <>
            <button
              type="button"
              onClick={() => {
                onSheetTargetChange("journal");
              }}
              className={cn(
                "flex min-w-0 flex-1 items-center justify-between gap-2 text-left active:opacity-70",
                rowPadding
              )}
            >
              {journalInner}
            </button>
            <span className="w-px self-stretch bg-secondary-bluegrey-2" />
          </>
        )}
        <button
          type="button"
          onClick={() => {
            onSheetTargetChange("evidence");
          }}
          className={cn(
            "flex min-w-0 flex-1 items-center justify-between gap-2 text-left active:opacity-70",
            rowPadding
          )}
        >
          {/* SIGN-OFF (Menie): the column states the STUDY DESIGN, not the
              conduct verdict. The design is the fact that qualifies a verdict,
              it is stable, and it reads without a scale to explain — so it is
              what the teaser carries, and the risk of bias waits inside the
              sheet where the card states what was assessed before grading it.
              A chevron, not a signal and a word: the column is a door now
              rather than a value. */}
          <span className="flex min-w-0 flex-col">
            <span className="text-footnote/snug font-semibold text-ds-black">
              {readingT("Study design")}
            </span>
            <span className="text-caption/snug text-ds-slate-600">
              {designLabel(readingT, level.design)}
            </span>
          </span>
          <IconChevronRight className="size-4 shrink-0 text-ds-slate-400" />
        </button>
      </div>
      {sheet}
    </section>
  );
}
